'use client';


import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { AlertCircle, BookOpen, Bookmark, CheckCircle2, Download, Feather, Lock, Mail, LoaderCircle, ShieldCheck } from 'lucide-react';

import { useAppAuth } from '@/hooks/use-app-auth';
import { ROUTES } from '@/lib/routes';
import { getUserInitials, roleSummary } from '@/lib/auth/user-docs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

function AuthShell({
  eyebrow,
  title,
  description,
  children,
  sideTitle = 'One account, one place',
  sidePoints = [
    'Use one login for books, downloads, and your library.',
    'Your account keeps purchases, profile details, and author tools connected.',
    'Lumina Press is coming soon. Your reader account is ready to use.',
  ],
}: {
  eyebrow: string;
  title: string;
  description: string;
  children?: ReactNode;
  sideTitle?: string;
  sidePoints?: string[];
}) {
  return (
    <div className="mx-auto flex min-h-[calc(100vh-7rem)] max-w-6xl items-center px-4 py-12">
      <div className="grid w-full gap-8 rounded-[2rem] border border-blush/70 bg-white p-8 shadow-sm lg:grid-cols-[1.1fr_0.9fr] lg:p-12">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-terra">{eyebrow}</p>
          <h1 className="mt-4 font-headline text-4xl text-charcoal md:text-5xl">{title}</h1>
          <p className="mt-4 max-w-2xl text-base leading-7 text-charcoal/75">{description}</p>
          <div className="mt-8">{children ?? null}</div>
        </div>
        <div className="rounded-[1.75rem] bg-cream p-6">
          <p className="text-sm font-semibold text-charcoal">{sideTitle}</p>
          <ul className="mt-4 space-y-3 text-sm leading-6 text-charcoal/75">
            {sidePoints.map((point) => (
              <li key={point}>• {point}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function FormMessage({ error, success }: { error?: string | null; success?: string | null }) {
  if (!error && !success) return null;
  const destructive = Boolean(error);
  return (
    <div
      data-auth-message={destructive ? 'error' : 'success'}
      role={destructive ? 'alert' : 'status'}
      aria-live={destructive ? 'assertive' : 'polite'}
      className={cn(
        'mb-4 flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm',
        destructive ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700',
      )}
    >
      {destructive ? <AlertCircle className="mt-0.5 h-4 w-4" /> : <CheckCircle2 className="mt-0.5 h-4 w-4" />}
      <span>{error ?? success}</span>
    </div>
  );
}

function ResolveNextPath(nextParam: string | null, fallback: string) {
  if (!nextParam) return fallback;
  if (!nextParam.startsWith('/') || nextParam.startsWith('//') || nextParam.includes('\\') || /[\u0000-\u0020\u007f]/.test(nextParam)) return fallback;
  return nextParam;
}

export function LoginPanel() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = ResolveNextPath(searchParams.get('next'), ROUTES.account);
  const { signIn, signInWithGoogle, session, firebaseReady, loading, sessionSyncError } = useAppAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (loading || !session) return;

    if (next.startsWith('/admin') && !session.canAccessAdmin) {
      router.replace(ROUTES.account);
      return;
    }

    if (next.startsWith('/studio') && !session.canAccessStudio) {
      router.replace(ROUTES.luminaPress);
      return;
    }

    if (!session.onboardingCompleted && next !== '/assessment/claim') {
      router.replace(`${ROUTES.completeProfile}?next=${encodeURIComponent(next)}`);
      return;
    }

    router.replace(next);
  }, [loading, next, router, session]);

  async function handleSignIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await signIn({ email, password, remember });
    setSubmitting(false);
    if (!result.ok) {
      setError(next === '/assessment/claim' ? 'Unable to continue with these details. Try signing in or use password recovery.' : result.error);
      return;
    }
    router.replace(next);
  }

  async function handleGoogle() {
    setSubmitting(true);
    setError(null);
    const result = await signInWithGoogle({ remember });
    setSubmitting(false);
    if (!result.ok) {
      setError(next === '/assessment/claim' ? 'Unable to continue with these details. Try signing in or use password recovery.' : result.error);
      return;
    }
    router.replace(next);
  }

  const accessPoints = [
    { label: 'Your books in one place', icon: BookOpen },
    { label: 'Reader library access', icon: Bookmark },
    { label: 'Your book library', icon: Feather },
    { label: 'Saved articles and downloads', icon: Download },
  ];

  return (
    <section className="bg-[#f8f2ec] px-4 py-[20px] md:px-8">
      <div className="mx-auto max-w-[1304px]">
        <div
          className="relative min-h-[797px] overflow-hidden rounded-[26px] border border-[#e1cfc0] bg-[#e9ddd1] shadow-[0_30px_100px_rgba(76,56,42,0.16)]"
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgba(245,236,226,0.03) 0%, rgba(245,236,226,0.2) 42%, rgba(245,236,226,0.82) 67%, rgba(245,236,226,0.96) 100%), url('https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?auto=format&fit=crop&w=1800&q=88')",
            backgroundPosition: 'left center',
            backgroundSize: 'cover',
          }}
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_10%,rgba(255,255,255,0.58),transparent_24%),radial-gradient(circle_at_48%_22%,rgba(255,255,255,0.22),transparent_30%),linear-gradient(90deg,rgba(255,250,242,0.08),rgba(255,250,242,0.42)_51%,rgba(255,250,242,0.88)_100%)]" />

          <div className="relative grid min-h-[797px] gap-7 p-[32px] lg:grid-cols-[1fr_516px]">
            <div className="flex min-h-[720px] flex-col justify-center px-5 py-10 sm:px-10 lg:pl-[250px] lg:pr-5">
              <div className="max-w-[430px]">
                <div className="mb-[28px] flex h-[42px] w-[42px] items-center justify-center text-[#bd7658]">
                  <Feather className="h-7 w-7 stroke-[1.15]" />
                </div>
                <h1 className="font-headline text-[clamp(2.95rem,4.4vw,4.7rem)] leading-[1.03] tracking-[-0.052em] text-[#263036]">
                  Welcome back to your quiet corner.
                </h1>
                <p className="mt-[28px] text-[17px] leading-[1.66] text-[#566163]">
                  Sign in to access your library, saved articles, and downloads.
                </p>
                <div className="mt-[27px] grid gap-[16px] text-[16px] text-[#364044]">
                  {accessPoints.map((point) => {
                    const Icon = point.icon;
                    return (
                      <div key={point.label} className="flex items-center gap-[17px]">
                        <span className="flex h-[25px] w-[25px] items-center justify-center text-[#b66c4c]">
                          <Icon className="h-[22px] w-[22px] stroke-[1.35]" />
                        </span>
                        <span>{point.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex min-h-[720px] flex-col justify-start gap-[18px]">
              <div className="rounded-[18px] border border-white/75 bg-white/84 px-[43px] py-[44px] shadow-[0_24px_70px_rgba(64,48,38,0.12)] backdrop-blur-md">
                <h2 className="font-headline text-[43px] leading-none tracking-[-0.04em] text-[#283137]">Sign in</h2>
                <div className="mt-[15px] h-px w-[90px] bg-[#c98267]" />
                <p className="mt-[23px] text-[15px] leading-7 text-[#596366]">
                  {firebaseReady ? 'Use your email or Google account to continue.' : 'Account sign-in is not available yet. Please try again shortly.'}
                </p>

                <FormMessage error={error ?? sessionSyncError} />

                <form className="mt-[22px] space-y-[21px]" onSubmit={handleSignIn}>
                  <div className="space-y-[9px]">
                    <Label htmlFor="login-email" className="text-[15px] font-medium text-[#283137]">
                      Email
                    </Label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-[15px] top-1/2 h-[20px] w-[20px] -translate-y-1/2 text-[#677071]" />
                      <Input
                        id="login-email"
                        type="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        placeholder="you@email.com"
                        required
                        className="h-[49px] rounded-[8px] border-[#d4c4b6] bg-white/86 pl-[47px] text-[15px] text-[#283137] shadow-inner shadow-black/[0.015] placeholder:text-[#919898]"
                      />
                    </div>
                  </div>

                  <div className="space-y-[9px]">
                    <div className="flex items-center justify-between gap-4">
                      <Label htmlFor="login-password" className="text-[15px] font-medium text-[#283137]">
                        Password
                      </Label>
                      <Link href={ROUTES.forgotPassword} className="text-[14px] font-medium text-[#b96041] hover:underline">
                        Forgot password?
                      </Link>
                    </div>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-[15px] top-1/2 h-[20px] w-[20px] -translate-y-1/2 text-[#677071]" />
                      <Input
                        id="login-password"
                        type="password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        required
                        className="h-[49px] rounded-[8px] border-[#d4c4b6] bg-white/86 pl-[47px] text-[15px] text-[#283137] shadow-inner shadow-black/[0.015]"
                      />
                    </div>
                  </div>

                  <label className="flex cursor-pointer items-center gap-[12px] text-[15px] text-[#3f494b]">
                    <input
                      id="login-remember"
                      name="remember"
                      type="checkbox"
                      checked={remember}
                      onChange={(event) => setRemember(event.target.checked)}
                      className="h-[20px] w-[20px] rounded border-[#b7a492] accent-[#5f8769]"
                    />
                    Keep me signed in
                  </label>

                  <Button disabled={submitting || !firebaseReady} type="submit" className="h-[50px] w-full rounded-[8px] bg-[#4f7d5e] text-[15px] font-medium text-white shadow-[0_12px_24px_rgba(79,125,94,0.23)] hover:bg-[#466f54]">
                    {submitting ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Sign in
                  </Button>
                </form>

                <div className="my-[25px] flex items-center gap-[17px] text-[14px] text-[#77716c]">
                  <span className="h-px flex-1 bg-[#dfcfc0]" />
                  <span>or</span>
                  <span className="h-px flex-1 bg-[#dfcfc0]" />
                </div>

                <Button disabled={submitting || !firebaseReady} onClick={handleGoogle} variant="outline" className="h-[49px] w-full rounded-[8px] border-[#d4c4b6] bg-white/80 text-[15px] font-medium text-[#2e373b] hover:bg-[#f7efe6]">
                  <span className="mr-3 inline-flex h-[23px] w-[23px] items-center justify-center rounded-full bg-white font-semibold text-[#4285f4] shadow-sm">G</span>
                  Continue with Google
                </Button>

                <p className="mt-[24px] text-center text-[15px] text-[#5f6768]">
                  New here?{' '}
                  <Link href={`${ROUTES.signup}${next ? `?next=${encodeURIComponent(next)}` : ''}`} className="font-medium text-[#b96041] hover:underline">
                    Create an account
                  </Link>
                </p>
              </div>

              <div className="rounded-[16px] border border-white/75 bg-white/58 p-[23px] shadow-[0_16px_44px_rgba(64,48,38,0.09)] backdrop-blur-md">
                <div className="flex items-center gap-[21px]">
                  <span className="flex h-[64px] w-[64px] shrink-0 items-center justify-center rounded-full bg-[#f4e4d2] text-[#b66c4c]">
                    <ShieldCheck className="h-[29px] w-[29px] stroke-[1.5]" />
                  </span>
                  <div>
                    <p className="font-headline text-[22px] leading-tight tracking-[-0.025em] text-[#283137]">One account, one place.</p>
                    <p className="mt-[8px] max-w-[330px] text-[14px] leading-6 text-[#566064]">
                      Calm access to your books, purchases, and writing tools.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function SignupPanel() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = ResolveNextPath(searchParams.get('next'), ROUTES.completeProfile);
  const { signUp, signInWithGoogle, firebaseReady, sessionSyncError } = useAppAuth();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const result = await signUp({ displayName, email, password, verificationNext: next });
    setSubmitting(false);
    if (!result.ok) {
      setError(next === '/assessment/claim' ? 'Unable to continue with these details. Try signing in or use password recovery.' : result.error);
      return;
    }
    toast({ title: 'Account created', description: next === '/assessment/claim' ? 'Verify your email to open your private Assessment.' : 'Verification email sent. Complete your profile next.' });
    router.push(next === '/assessment/claim' ? `${ROUTES.verifyEmail}?next=${encodeURIComponent(next)}` : `${ROUTES.completeProfile}?next=${encodeURIComponent(next)}`);
  }

  async function handleGoogle() {
    setSubmitting(true);
    setError(null);
    const result = await signInWithGoogle();
    setSubmitting(false);
    if (!result.ok) {
      setError(next === '/assessment/claim' ? 'Unable to continue with these details. Try signing in or use password recovery.' : result.error);
      return;
    }
    router.push(next === '/assessment/claim' ? `${ROUTES.verifyEmail}?next=${encodeURIComponent(next)}` : `${ROUTES.completeProfile}?next=${encodeURIComponent(next)}`);
  }

  return (
    <AuthShell
      eyebrow="Shared sign-up"
      title={next === '/assessment/claim' ? 'Create your private account' : 'Create a reader or author account'}
      description="Create one account for books, downloads, and your library. Lumina Press is coming soon."
    >
      <Card className="rounded-[1.5rem] border border-blush/70 shadow-none">
        <CardHeader>
          <CardTitle className="font-headline text-2xl text-charcoal">Create account</CardTitle>
          <CardDescription className="text-charcoal/70">Use email/password or Google to access your account and library.</CardDescription>
        </CardHeader>
        <CardContent>
          <FormMessage error={error ?? sessionSyncError} />
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="signup-name">Display name</Label>
              <Input id="signup-name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-email">Email</Label>
              <Input id="signup-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-password">Password</Label>
              <Input id="signup-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-confirm-password">Confirm password</Label>
              <Input id="signup-confirm-password" type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} minLength={8} required />
            </div>
            <Button disabled={submitting || !firebaseReady} type="submit" className="w-full bg-[#5F8769] text-white hover:bg-[#547A5F]">
              {submitting ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
              Create account
            </Button>
          </form>
          <div className="my-4 h-px bg-blush/70" />
          <Button disabled={submitting || !firebaseReady} onClick={handleGoogle} variant="outline" className="w-full border-blush bg-cream text-charcoal hover:bg-blush/60">
            Continue with Google
          </Button>
          <p className="mt-4 text-sm text-charcoal/70">
            Already have an account?{' '}
            <Link href={`${ROUTES.login}${next ? `?next=${encodeURIComponent(next)}` : ''}`} className="font-medium text-terra hover:underline">
              Sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </AuthShell>
  );
}

export function ForgotPasswordPanel() {
  const { sendPasswordReset, firebaseReady } = useAppAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    const result = await sendPasswordReset(email);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setSuccess('Reset email sent. Check your inbox for the password reset link.');
  }

  return (
    <AuthShell
      eyebrow="Password recovery"
      title="Recover account access"
      description="Enter your email and we will send a secure password reset link."
    >
      <Card className="rounded-[1.5rem] border border-blush/70 shadow-none">
        <CardHeader>
          <CardTitle className="font-headline text-2xl text-charcoal">Reset password</CardTitle>
          <CardDescription className="text-charcoal/70">Enter the email address attached to your account.</CardDescription>
        </CardHeader>
        <CardContent>
          <FormMessage error={error} success={success} />
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email</Label>
              <Input id="forgot-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            </div>
            <Button disabled={submitting || !firebaseReady} type="submit" className="w-full bg-[#5F8769] text-white hover:bg-[#547A5F]">
              {submitting ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
              Send reset email
            </Button>
          </form>
        </CardContent>
      </Card>
    </AuthShell>
  );
}

export function ResetPasswordPanel() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const code = searchParams.get('oobCode') ?? '';
  const mode = searchParams.get('mode');
  const { resetPasswordWithCode, firebaseReady } = useAppAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!code || mode !== 'resetPassword') {
      setError('This reset link is missing the required password reset code.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    const result = await resetPasswordWithCode(code, password);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setSuccess('Password updated successfully. You can sign in now.');
    setTimeout(() => router.push(ROUTES.login), 1000);
  }

  return (
    <AuthShell
      eyebrow="Password update"
      title="Set a new password"
      description="Use the reset link from your email to choose a new password."
    >
      <Card className="rounded-[1.5rem] border border-blush/70 shadow-none">
        <CardHeader>
          <CardTitle className="font-headline text-2xl text-charcoal">Create new password</CardTitle>
          <CardDescription className="text-charcoal/70">Choose a strong password with at least 8 characters.</CardDescription>
        </CardHeader>
        <CardContent>
          <FormMessage error={error} success={success} />
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="reset-password">New password</Label>
              <Input id="reset-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reset-confirm-password">Confirm password</Label>
              <Input id="reset-confirm-password" type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} minLength={8} required />
            </div>
            <Button disabled={submitting || !firebaseReady} type="submit" className="w-full bg-[#5F8769] text-white hover:bg-[#547A5F]">
              {submitting ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save new password
            </Button>
          </form>
        </CardContent>
      </Card>
    </AuthShell>
  );
}

export function VerifyEmailPanel() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const code = searchParams.get('oobCode');
  const mode = searchParams.get('mode');
  let verificationNext = searchParams.get('next');
  try {
    const continuation = new URL(searchParams.get('continueUrl') || '', typeof window === 'undefined' ? 'https://theuntangled.life' : window.location.origin);
    if (continuation.pathname === '/verify-email') verificationNext ||= continuation.searchParams.get('next');
  } catch { /* A missing continuation uses the normal account destination. */ }
  const next = ResolveNextPath(verificationNext, ROUTES.account);
  const { session, user, signOutUser, applyEmailVerificationCode, resendVerificationEmail, refreshSession, firebaseReady } = useAppAuth();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!code || mode !== 'verifyEmail') return;

    let active = true;
    void (async () => {
      setWorking(true);
      const result = await applyEmailVerificationCode(code);
      if (!active) return;
      setWorking(false);
      if (!result.ok) {
        setError(result.error);
        return;
      }
      setSuccess('Email verified successfully.');
      await refreshSession();
    })();

    return () => {
      active = false;
    };
  }, [applyEmailVerificationCode, code, mode, refreshSession]);

  async function handleResend() {
    setWorking(true);
    setError(null);
    setSuccess(null);
    const result = await resendVerificationEmail(next);
    setWorking(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setSuccess('Verification email sent again.');
  }

  const verified = session?.emailVerified;

  return (
    <AuthShell
      eyebrow="Email verification"
      title="Confirm your email address"
      description="Confirming your email helps protect your account and makes recovery easier."
    >
      <Card className="rounded-[1.5rem] border border-blush/70 shadow-none">
        <CardHeader>
          <CardTitle className="font-headline text-2xl text-charcoal">Email verification</CardTitle>
          <CardDescription className="text-charcoal/70">
            {verified ? 'This account is already verified.' : 'Use the link from your email or request a fresh verification message.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormMessage error={error} success={success ?? (verified ? 'Your email is already verified.' : null)} />
          <div className="rounded-2xl bg-cream p-4 text-sm text-charcoal/75">
            Signed in as <span className="font-medium text-charcoal">{session?.email ?? user?.email ?? 'No active session'}</span>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button disabled={working || !firebaseReady || !user || verified} onClick={handleResend} className="bg-[#5F8769] text-white hover:bg-[#547A5F]">
              {working ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
              Resend verification
            </Button>
            <Button variant="outline" className="border-blush bg-cream text-charcoal hover:bg-blush/60" onClick={() => router.push(next)}>
              Continue
            </Button>
            {next === '/assessment/claim' && session && !user ? <Button variant="outline" onClick={() => void signOutUser().then(result => { if (result.ok) router.push(`/login?next=${encodeURIComponent(next)}`); else setError(result.error); })}>Sign in again to refresh verification</Button> : null}
          </div>
        </CardContent>
      </Card>
    </AuthShell>
  );
}

export function CompleteProfilePanel() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = ResolveNextPath(searchParams.get('next'), ROUTES.account);
  const { firebaseReady, loading, user, profile, completeProfileFlow, sessionSyncError } = useAppAuth();
  const [displayName, setDisplayName] = useState('');
  const [accountMode, setAccountMode] = useState<'reader' | 'author'>('reader');
  const [penName, setPenName] = useState('');
  const [bioShort, setBioShort] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDisplayName(profile?.displayName ?? user?.displayName ?? '');
      const shouldDefaultToAuthor = Boolean(profile?.roles?.includes('author')) || next.startsWith('/studio');
      setAccountMode(shouldDefaultToAuthor ? 'author' : 'reader');
      setPenName(profile?.displayName ?? user?.displayName ?? '');
    }, 0);
    return () => window.clearTimeout(handle);
  }, [next, profile, user]);

  useEffect(() => {
    if (!loading && !user) {
      router.replace(`${ROUTES.login}?next=${encodeURIComponent(`${ROUTES.completeProfile}?next=${encodeURIComponent(next)}`)}`);
    }
  }, [loading, next, router, user]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (accountMode === 'author' && !penName.trim()) {
      setError('Authors need a pen name or public author display name.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const result = await completeProfileFlow({ displayName, accountMode, penName, bioShort });
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    router.push(next.startsWith('/studio') && accountMode === 'author' ? next : next);
  }

  const roleBadges = useMemo(() => (profile?.roles ?? []).map((role) => roleSummary([role])), [profile?.roles]);

  return (
    <AuthShell
      eyebrow="Profile bootstrap"
      title="Complete your reader or author profile"
      description="Set up your reader or author profile. Lumina Press is coming soon; creating an author profile does not unlock the private studio."
    >
      <Card className="rounded-[1.5rem] border border-blush/70 shadow-none">
        <CardHeader>
          <CardTitle className="font-headline text-2xl text-charcoal">Finish onboarding</CardTitle>
          <CardDescription className="text-charcoal/70">Choose how this account should enter the platform first. You can still buy books either way.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-6 flex items-center gap-4 rounded-[1.5rem] bg-cream p-4">
            <Avatar className="h-12 w-12 border border-blush/70">
              <AvatarFallback className="bg-white text-charcoal">{getUserInitials(profile?.displayName ?? user?.displayName, user?.email)}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-charcoal">{profile?.displayName ?? user?.displayName ?? 'Untangled User'}</p>
              <p className="text-sm text-charcoal/70">{user?.email}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {(roleBadges.length ? roleBadges : ['Reader']).map((badge) => (
                  <Badge key={badge} variant="secondary" className="bg-white text-charcoal/80">
                    {badge}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          <FormMessage error={error ?? sessionSyncError} />
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="profile-display-name">Display name</Label>
              <Input id="profile-display-name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} required />
            </div>

            <div className="space-y-3">
              <Label>Choose starting mode</Label>
              <div className="grid gap-3 md:grid-cols-2">
                {[
                  {
                    value: 'reader' as const,
                    title: 'Reader account',
                    description: 'Use the storefront, library, and orders center first. You can become an author later.',
                  },
                  {
                    value: 'author' as const,
                    title: 'Author account',
                    description: 'Create your author profile. Lumina Press tools are not yet open to the public.',
                  },
                ].map((option) => {
                  const active = accountMode === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setAccountMode(option.value)}
                      className={cn(
                        'rounded-[1.5rem] border p-4 text-left transition-colors',
                        active ? 'border-[#5F8769] bg-[#5F8769]/5' : 'border-blush/70 bg-white hover:bg-cream',
                      )}
                    >
                      <p className="font-medium text-charcoal">{option.title}</p>
                      <p className="mt-2 text-sm leading-6 text-charcoal/75">{option.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {accountMode === 'author' ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="profile-pen-name">Pen name / public author name</Label>
                  <Input id="profile-pen-name" value={penName} onChange={(event) => setPenName(event.target.value)} required={accountMode === 'author'} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="profile-bio">Short bio</Label>
                  <Textarea id="profile-bio" value={bioShort} onChange={(event) => setBioShort(event.target.value)} placeholder="Short author bio for your future public profile" />
                </div>
              </>
            ) : null}

            <Button disabled={submitting || !firebaseReady || !user} type="submit" className="w-full bg-[#5F8769] text-white hover:bg-[#547A5F]">
              {submitting ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save profile
            </Button>
          </form>
        </CardContent>
      </Card>
    </AuthShell>
  );
}
